import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { SwipeCard } from '../components/SwipeCard';
import { Loader2, ArrowLeft, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';
import { HealthCrossLogo } from '../components/HealthCrossLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SwipeDiagnosis = () => {
  const { symptomId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, [symptomId]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions?symptom_id=${symptomId}`);
      if (response.data.length === 0) {
        toast.error('No questions available for this symptom');
        navigate('/symptoms');
        return;
      }
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
      navigate('/symptoms');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    const newAnswers = [
      ...answers,
      { question_id: questions[currentIndex].id, answer }
    ];
    setAnswers(newAnswers);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Submit diagnosis
      setSubmitting(true);
      try {
        // Create headers with token if logged in
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.post(`${API}/diagnoses`, {
          symptom_id: symptomId,
          answers: newAnswers
        }, { headers });
        
        toast.success(t('assessmentResults') + '!');
        
        // If user is logged in, navigate to saved result
        // Otherwise, pass data through state
        if (user) {
          navigate(`/results/${response.data.id}`);
        } else {
          navigate('/results', { state: { diagnosis: response.data } });
        }
      } catch (error) {
        console.error('Failed to submit diagnosis:', error);
        toast.error('Failed to submit assessment');
        setSubmitting(false);
      }
    }
  };

  // Guest header
  const GuestHeader = () => (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate('/symptoms')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <HealthCrossLogo className="w-10 h-10" iconClassName="w-5 h-5" />
          <span className="font-bold text-lg hidden sm:block">{t('appName')}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate('/auth')}
          >
            {t('signIn')}
          </Button>
        </div>
      </div>
    </header>
  );

  if (loading || submitting) {
    return (
      <>
        {!user && <GuestHeader />}
        <div className={`min-h-[80vh] flex items-center justify-center ${!user ? 'pt-16' : ''}`} data-testid="swipe-loading">
          <Toaster position="top-center" />
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">
              {submitting ? t('analyzingResponses') : t('loadingQuestions')}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!user && <GuestHeader />}
      <div className={`max-w-md mx-auto px-6 py-8 ${!user ? 'pt-24' : ''}`} data-testid="swipe-diagnosis">
        <Toaster position="top-center" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-6"
        >
          <span className="label-medical">{t('step2')}</span>
          <h1 className="text-2xl font-bold mt-2">{t('answerQuestions')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('swipeRightYes')}
          </p>
        </motion.div>

        <div className="swipe-container">
          <div className="swipe-card-stack">
            <AnimatePresence>
              {questions.map((question, index) => {
                if (index < currentIndex) return null;
                return (
                  <div
                    key={question.id}
                    className="swipe-card-wrapper"
                    style={{ zIndex: questions.length - index }}
                  >
                    <SwipeCard
                      question={question}
                      onAnswer={handleAnswer}
                      isTop={index === currentIndex}
                      totalCards={questions.length}
                      currentIndex={currentIndex}
                    />
                  </div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};
